import { useCompanyApi } from '@/api/client/api-client';
import type { EarlyDeposit } from '@/api/interfaces/early-deposit.types';
import { notifyError } from '@/bugsnag';
import type {
    AddFileErrorKey,
    ClientSideValidationErrors,
    ErrorValueArray,
    ServerErrorResponse,
    ServerErrors,
} from '@/company-portal/add-file/add-file-form-errors.types';
import { buildAddFileFormData, mapToConfirmationClient, mapToSubmitClients } from '@/company-portal/add-file/add-file-utils';
import type {
    AddFileClient,
    AddFileRouteTypes,
    AddFileSubmissionResponse,
    AddFileSubmitRequest,
    AddFileValidateRequest,
    TPSOrderInfo,
} from '@/company-portal/add-file/add-file.types';
import { csrfToken } from '@/components/Utils/Helper';
import type {
    AddFileConfirmationSummary,
    AvailableWireDoc,
    File,
    UserWithBranchesGroup,
} from '@/components/Views/Company/File/file.types';
import { calendarDateToNativeDate } from '@/shared/utils/calendar-date-utils';
import { formatDate, MONTH_DAY_YEAR_FORMAT } from '@/shared/utils/format-date';
import { formatCentsAsMoney } from '@/shared/utils/format-number';
import { safeRedirect } from '@/shared/utils/safe-redirect';
import { useFileClientStore } from '@/stores/company/file-client.store';
import { useFileDocumentStore } from '@/stores/file-document.store';
import type { Branch, Company } from '@/types/closinglock.types';
import { isArray } from 'lodash';
import { defineStore } from 'pinia';
import type { DateValue } from 'reka-ui';
import { v4 as uuidV4 } from 'uuid';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue, watch } from 'vue';
import { useRouter } from 'vue-router';

const UNKNOWN_ERROR: ServerErrorResponse = { message: 'We ran into an unexpected error. Please try again shortly.' };

export const useAddFileStore = defineStore('addFile', () => {
    const companyApi = useCompanyApi({ auth: 'jwt' });

    const fileDocumentStore = useFileDocumentStore();
    const fileClientStore = useFileClientStore();

    const router = useRouter();
    const isFormDisabled = ref(false);

    // file details
    const refNumber = ref('');
    const address = ref('');
    const company = ref<Company>();
    const branch = ref<Branch>();
    const closeDate = ref<DateValue>();
    const balanceDue = ref<number>();
    const notes = ref('');

    // send wire instructions
    const wireDoc = ref<AvailableWireDoc>();
    const wireDocRecipientClientIds = ref<AddFileClient['id'][]>([]);

    // request wire instructions
    const requestWireInfoClientIds = ref<AddFileClient['id'][]>([]);

    // request IDV
    const requestIdvClientIds = ref<AddFileClient['id'][]>([]);

    // notifications
    const notificationUsers = ref<UserWithBranchesGroup[]>([]);
    const notifyClients = ref(true);

    // TPS prepopulated order
    const tpsOrder = ref<TPSOrderInfo>();

    // EMD merge inputs
    const mergeEmdId = ref<EarlyDeposit['id']>('');
    const mergeEmdFormData = ref<FormData>();

    /** @todo remove in CLENG-1787 */
    const clientsSectionRemountKey = ref(uuidV4());

    const confirmationSummary = computed<AddFileConfirmationSummary>(() => ({
        fileDetails: {
            referenceNumber: refNumber.value,
            propertyAddress: address.value,
            company: company.value?.name ?? '',
            branch: branch.value?.name ?? '',
            closingDate: closeDate.value ? formatDate(calendarDateToNativeDate(closeDate.value)) : '',
            balanceDueFormatted: balanceDue.value ? formatCentsAsMoney(balanceDue.value) : '',
        },
        clients: fileClientStore.availableClientOptions
            .map(clientOption =>
                mapToConfirmationClient({
                    clientOption,
                    documentConfigs: fileDocumentStore.selectedDocumentConfigs,
                    requestWireInfoClientIds: requestWireInfoClientIds.value,
                    requestIdvClientIds: requestIdvClientIds.value,
                    wireDocRecipientIds: wireDocRecipientClientIds.value,
                    wireDoc: wireDoc.value,
                }),
            )
            // Only clients with a task (recipient, request-wire-info, or IDV) belong on the file.
            .filter(client => client.included),
    }));

    const loadTpsOrder = (tpsOrderRoute: MaybeRefOrGetter<string>) => {
        const {
            data: tpsOrderData,
            isFetching: isLoadingTpsOrder,
            execute: getTpsOrder,
        } = companyApi(tpsOrderRoute, {
            refetch: true,
            immediate: false,
            beforeFetch(ctx) {
                if (!toValue(tpsOrderRoute)) ctx.cancel();
            },
            onFetchError(ctx) {
                notifyError(ctx.error, 'addFileStore::loadTpsOrder', {
                    ctx,
                });
                return ctx;
            },
        }).json<TPSOrderInfo>();

        watch(tpsOrderData, newTpsOrderData => (tpsOrder.value = newTpsOrderData ?? undefined));

        return {
            getTpsOrder,
            isLoadingTpsOrder,
        };
    };

    // submission / validation errors
    const submissionErrors = ref<ServerErrorResponse>();
    const extraErrorCounterMessageRegex = /\(and \d+.*\)/gi; // filters out lone `(and 2 more)` messages that have no other detail
    const rootErrorMessage = computed(() =>
        submissionErrors.value?.message?.replaceAll(extraErrorCounterMessageRegex, '')?.trim(),
    );
    const serverValidationErrors = computed(() => submissionErrors.value?.errors);
    const clearServerValidationError = (key: AddFileErrorKey) => {
        let errorMessage: ErrorValueArray | undefined;
        if (submissionErrors.value?.errors) {
            errorMessage = submissionErrors.value.errors[key];
            delete submissionErrors.value.errors[key];
            clearRootErrorMessageIfMatchesClearedField(errorMessage);
        }
    };
    const clearRootErrorMessageIfMatchesClearedField = (errorMessage?: ErrorValueArray) => {
        const rootMessage = rootErrorMessage.value;
        if (!rootMessage || !isArray(errorMessage)) {
            return;
        }

        const removedErrorContainsRoot = errorMessage.some(message => message.trim().indexOf(rootMessage) >= 0);
        if (removedErrorContainsRoot) {
            submissionErrors.value = {
                ...submissionErrors.value,
                message: undefined,
            };
        }
    };

    const clearSubmissionErrors = () => {
        submissionErrors.value = undefined;
        fileDocumentStore.esignEditError = undefined;
    };

    // Merge server or client-side (preemptive) validation errors into the existing error state.
    const applySubmissionErrors = (errors: Partial<ServerErrors | ClientSideValidationErrors>) => {
        submissionErrors.value = {
            ...submissionErrors.value,
            errors: { ...submissionErrors.value?.errors, ...errors },
        };
    };

    const buildValidateRequest = (): AddFileValidateRequest => {
        return {
            token: csrfToken.value,
            refNumber: refNumber.value,
            address: address.value,
            companyId: company.value?.id,
            branchId: branch.value?.id,
            closeDate: closeDate.value ? formatDate(calendarDateToNativeDate(closeDate.value), MONTH_DAY_YEAR_FORMAT) : '',
            balanceDue: balanceDue.value ? formatCentsAsMoney(balanceDue.value) : undefined,
            notes: notes.value,
            wireDocId: wireDoc.value?.id,
            notify: notifyClients.value,
            clients: mapToSubmitClients({
                availableClientOptions: fileClientStore.startedClientOptions,
                documentConfigs: fileDocumentStore.selectedDocumentConfigs,
                requestWireInfoClientIds: requestWireInfoClientIds.value,
                requestIdvClientIds: requestIdvClientIds.value,
                wireDocRecipientIds: wireDocRecipientClientIds.value,
                // Keep tasked clients (the payload) plus clients without a role (so the role guard can
                // flag them). On a valid submit no items without a clients remain, so the payload is unchanged.
            }).filter(client => client.included || !client.role),
            wireDocRecipientIds: wireDocRecipientClientIds.value.map(String),
            requestBankInfoRecipientIds: requestWireInfoClientIds.value.map(String),
            requestIdvRecipientIds: requestIdvClientIds.value.map(String),
            notificationUserIds: notificationUsers.value.map(user => Number(user.value)),
            vendor: tpsOrder.value
                ? {
                      vendor: tpsOrder.value.vendor,
                      vendor_order_id: tpsOrder.value.vendor_order_id,
                      vendor_transaction_id: tpsOrder.value.vendor_transaction_id,
                      amount_down: tpsOrder.value.amount_down ?? '',
                      earnest_money: tpsOrder.value.earnest_money ?? '',
                      sales_price: tpsOrder.value.sales_price,
                  }
                : undefined,
        };
    };

    // Exposed so the client-role guard can validate the outgoing clients before any server call.
    const validateRequest = computed(() => buildValidateRequest());

    const buildSubmitRequest = (): AddFileSubmitRequest => ({
        ...buildValidateRequest(),
        documents: fileDocumentStore.getFormUploadFormDataV1(),
    });

    const buildSubmitFormData = (): FormData => buildAddFileFormData(buildSubmitRequest());

    const redirectFromResponse = (data: AddFileSubmissionResponse | null) => {
        if (!data) {
            return;
        }

        const { href } = router.resolve({
            name: 'file-detail',
            params: {
                id: data.id,
            },
            query: {
                toastMessage: encodeURIComponent('New file created successfully'),
            },
        });
        safeRedirect(href);
    };

    const createValidateOperation = (route: MaybeRefOrGetter<AddFileRouteTypes['validate']>) => {
        const validateOperation = companyApi(route, { immediate: false, updateDataOnError: true })
            .post(computed(() => buildAddFileFormData(buildValidateRequest())))
            .json<AddFileSubmissionResponse>();
        validateOperation.onFetchError(ctx => {
            submissionErrors.value = validateOperation.data.value ?? UNKNOWN_ERROR;
            notifyError(ctx.error, 'useAddFileStore::validateFile', { ctx });
            return ctx;
        });
        return validateOperation;
    };

    const createSubmitOperation = (route: MaybeRefOrGetter<AddFileRouteTypes['submit']>) => {
        const submitOperation = companyApi(route, { immediate: false, updateDataOnError: true })
            .post(computed(buildSubmitFormData))
            .json<AddFileSubmissionResponse>();
        submitOperation.onFetchResponse(() => redirectFromResponse(submitOperation.data.value));
        submitOperation.onFetchError(ctx => {
            submissionErrors.value = submitOperation.data.value ?? UNKNOWN_ERROR;
            notifyError(ctx.error, 'useAddFileStore::createFile', { ctx });
            return ctx;
        });
        return submitOperation;
    };

    const createMergeWithEMDOperation = () => {
        const url = computed(() => `/user/early-deposit/${mergeEmdId.value}/file/merge`);
        const mergeOperation = companyApi(url, { immediate: false, updateDataOnError: true })
            .post(mergeEmdFormData)
            .json<AddFileSubmissionResponse>();

        mergeOperation.onFetchResponse(() => redirectFromResponse(mergeOperation.data.value));
        mergeOperation.onFetchError(ctx => {
            submissionErrors.value = mergeOperation.data.value ?? UNKNOWN_ERROR;
            notifyError(ctx.error, 'useAddFileStore::mergeFile', { ctx });
            return ctx;
        });
        return mergeOperation;
    };

    const createSimilarEMDSearchOperation = () => {
        const url = computed(() => `/user/early-deposit/search?address=${encodeURIComponent(address.value)}`);
        const similarEMDSearchOperation = companyApi(url, { immediate: false }).json<{ data: EarlyDeposit[] }>();
        similarEMDSearchOperation.onFetchError(ctx => notifyError(ctx.error, 'useAddFileStore::searchSimilarEmds', { ctx }));
        return similarEMDSearchOperation;
    };

    const resetAddFileStore = () => {
        submissionErrors.value = undefined;
        refNumber.value = '';
        address.value = '';
        notificationUsers.value = [];
        notifyClients.value = true;
        closeDate.value = undefined;
        balanceDue.value = undefined;
        notes.value = '';
        wireDocRecipientClientIds.value = [];
        requestWireInfoClientIds.value = [];
        requestIdvClientIds.value = [];
        fileDocumentStore.clearAllDocuments();
        fileClientStore.resetClients();
        clientsSectionRemountKey.value = uuidV4(); /** @todo remove in CLENG-4993 */
    };

    const checkRefNumberIsUnique = (refNumberToCheck: Ref<string>, excludeFileId?: MaybeRefOrGetter<File['id']>) => {
        const url = computed(() => {
            if (!refNumberToCheck.value) return '';
            const fileId = toValue(excludeFileId);
            const params = new URLSearchParams({
                ref_number: refNumberToCheck.value,
                exclude_file_id: fileId?.toString() || '',
            });
            return `/api/user/files/validate/ref-number-in-use?${params.toString()}`;
        });

        const { data, isFetching } = companyApi(url, {
            refetch: true,
            immediate: false,
            beforeFetch(ctx) {
                if (!refNumberToCheck.value) ctx.cancel();
            },
            onFetchError(ctx) {
                notifyError(ctx.error, 'useAddFileStore::checkRefNumberIsUnique', {
                    ctx,
                    url: url.value,
                });
                return ctx;
            },
        }).json<{ data: { matchingFileId: number | null } }>();

        return {
            checkResult: data,
            isChecking: isFetching,
        };
    };

    return {
        // global control
        isFormDisabled,

        // file details
        refNumber,
        address,
        company,
        branch,
        closeDate,
        balanceDue,
        notes,

        // send wire instructions
        wireDoc,
        wireDocRecipientClientIds,

        // request wire instructions
        requestWireInfoClientIds,

        // request IDV
        requestIdvClientIds,

        // notifications
        notificationUsers,
        notifyClients,

        // tps
        tpsOrder,
        loadTpsOrder,

        // form submission
        checkRefNumberIsUnique,
        confirmationSummary,
        validateRequest,
        resetAddFileStore,

        // error handling
        submissionErrors,
        rootErrorMessage,
        serverValidationErrors,
        clearServerValidationError,
        clearSubmissionErrors,
        applySubmissionErrors,

        // validate, merge and submit operations
        createValidateOperation,
        createMergeWithEMDOperation,
        buildSubmitFormData,
        createSubmitOperation,
        mergeEmdId,
        mergeEmdFormData,
        createSimilarEMDSearchOperation,

        /** @todo remove in CLENG-1787 */
        clientsSectionRemountKey,
    };
});
